import z from "zod";
import type { PartnerFlow } from "@remi/enums/partner-flow";
import type { SunrunAutomationStatus } from "@remi/enums/sunrun-automation-status";
import type { PartnerContactProjectRole } from "@remi/types/projectRole";
import type { SunrunAccountId } from "@remi/types/puppeteer";
import type { TaskAutomationRequeueBlockedReason } from "@remi/types/sunrun-automation";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const partnerIntegrationErrorTypeValues = [
  "UNKNOWN",
  "BID_NOT_FOUND",
  "PROJECT_NOT_FOUND",
  "ACCOUNT_ID_NOT_FOUND",
  "REQUEST_FOR_QUOTE_NOT_FOUND",
  "FILE_NOT_FOUND",
  "FILE_NOT_FOUND_IN_S3",
  "MULTIPLE_BID_REQUESTS_FOUND",
  "HOMEOWNER_HAS_NO_PHONE_NUMBER",
  "BID_REQUEST_NOT_FOUND",
  "ESTIMATE_PDF_NOT_FOUND",
  "EMAIL_NOTIFICATION_DATA_MISSING",
  "INVALID_TASK_STATE",
  "INSURANCE_APPROVAL_TASK_NOT_FOUND",
  "FAILED_TO_HANDLE_INSURANCE_FILES",
  "FAILED_TO_UPLOAD_FILES",
  "CLAIM_NUMBER_NOT_FOUND",
  "ACTIVE_HIC_NOT_FOUND",
  "HIC_NOT_SIGNED",
  "TASK_NOT_FOUND",
  "FAILED_TO_PROCESS_FILES",
  "FAILED_TO_CREATE_SVIX_APP",
  "FAILED_TO_FETCH_OPENAPI_SPEC",
  "UNSUPPORTED_PARTNER_FLOW",
  // sunrun automations
  "AUTOMATION_NOT_FOUND",
  "INVALID_AUTOMATION_DATA",
  "AUTOMATION_REQUEUE_NOT_ALLOWED",
  "AUTOMATION_REQUEUE_RACED",
  "AUTOMATION_REQUEUE_SEND_FAILED",
  "AUTOMATION_SEND_FAILED",
  "INVALID_ARGUMENTS",
  "PROJECT_NOT_CHILD_OF_ANY_BID",
  "PROJECT_NOT_CHILD_OF_BID",
  "MANUAL_RESOLUTION_REASON_REQUIRED",
  "FAILED_TO_UPDATE_AUTOMATION_STATUS",
  "NO_ACTIVE_QUOTE",
  "QUOTE_NOT_ACTIVE",
  "FAILED_TO_RESOLVE_AUTOMATIONS",
  "INVALID_TASK_TYPE",
  "INSURANCE_ELECTIVE_WORK_CREATED_TASK_NOT_FOUND",
  "ORGANIZATION_NOT_FOUND",
  "EXTERNAL_QUOTE_NOT_FOUND",
  "SHARINPIX_IMAGE_NOT_FOUND",
  "SHARINPIX_NO_CLICKABLE_LINK",
  "INVALID_EXTERNAL_SERVICE_TYPE",
  "INVALID_PARTNER_TASK_STATUS",
  "INVALID_WORK_ORDER_STATUS",
  "WORK_ORDER_NOT_FOUND",
  "WORK_ORDER_TYPE_NOT_FOUND",
  "ESTIMATE_NOT_FOUND",
  "FAILED_TO_CREATE_MESSAGE_IN_REMI",
  "WESTHILL_BID_SYNC_FAILED",
  "WESTHILL_SET_PARTNER_CONTACT_FAILED",
  "WESTHILL_ASSIGNMENT_SCRAPE_FAILED",
  "WESTHILL_XA_DOCUMENT_CHECK_DENIED",
  "WESTHILL_XA_DOCUMENT_UNCHECKABLE_NOTE_FAILED",
  "WESTHILL_XA_DOCUMENT_CHECK_FAILED",
  "XA_FILE_FETCH_FAILED",
  "RDCL_SITE_SURVEY_SUBMISSION_NOT_FOUND",
  "RDCL_SUBMISSION_SYNC_FAILED",
  "ENSITE_SITE_SURVEY_SUBMISSION_NOT_FOUND",
  "SUNRUN_RESCHEDULE_TASK_EVENT_NOT_FOUND",
  "ENSITE_SUBMISSION_MISSING_SLOT",
  "HOVER_TOKEN_REFRESH_FAILED",
  "HOVER_JOB_NOT_FOUND",
  "HOVER_SYNC_FILES_WORK_SITE_MISMATCH",
  "HOVER_AUTO_ASSIGN_PARTIAL_FAILURE",
  "HOVER_UNKNOWN_MODEL_STATE",
  "ATTACHMENT_BODY_TOO_LARGE",
  "UNSUPPORTED_SITE_VISIT_CONTRACTOR_ORG",
  "SITE_VISIT_AVAILABILITY_INTEGRATION_MISMATCH",
  "RDCL_REINSPECTION_MISSING_DATE_CREATED",
  "RDCL_REINSPECTION_CUTOFF_NOT_CONFIGURED",
  "RDCL_INSPECTION_PARSE_DRIFT",
  "RDCL_REINSPECTION_NO_PENDING_MAPPING",
  "RDCL_REINSPECTION_NOTIFICATION_EMAIL_NOT_CONFIGURED",
  "QUOTE_VERSION_NOT_FOUND",
  "SALESFORCE_CREDENTIALS_UNRECOVERABLE",
  "PARTNER_TASK_INGEST_IN_PROGRESS",
  "PARTNER_TASK_ATTACHMENT_DOWNLOAD_INCOMPLETE",
] as const;
const partnerIntegrationErrorTypeSchema = z.enum(partnerIntegrationErrorTypeValues);
const PartnerIntegrationErrorType = partnerIntegrationErrorTypeSchema.enum;
type PartnerIntegrationErrorType = z.infer<typeof partnerIntegrationErrorTypeSchema>;

export type PartnerIntegrationErrors =
  | { errorType: typeof PartnerIntegrationErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof PartnerIntegrationErrorType.BID_NOT_FOUND; data: { projectId: number } }
  | { errorType: typeof PartnerIntegrationErrorType.PROJECT_NOT_FOUND; data: { projectId?: number; taskId?: string } }
  | {
      errorType: typeof PartnerIntegrationErrorType.ACCOUNT_ID_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.REQUEST_FOR_QUOTE_NOT_FOUND;
      data: { rfqId?: number; projectId?: number; bidQuoteId?: string; quoteId?: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.QUOTE_VERSION_NOT_FOUND;
      data: { quoteVersionId?: string; quoteId?: string; activeQuoteId?: string | null };
    }
  | { errorType: typeof PartnerIntegrationErrorType.FILE_NOT_FOUND; data: { fileId: string } }
  | { errorType: typeof PartnerIntegrationErrorType.FILE_NOT_FOUND_IN_S3; data: { fileId: string } }
  | {
      errorType: typeof PartnerIntegrationErrorType.MULTIPLE_BID_REQUESTS_FOUND;
      data: { externalId: string };
    }
  | { errorType: typeof PartnerIntegrationErrorType.HOMEOWNER_HAS_NO_PHONE_NUMBER; data: { bidQuoteId: string } }
  | {
      errorType: typeof PartnerIntegrationErrorType.BID_REQUEST_NOT_FOUND;
      data: {
        externalId?: string;
        orgId?: string;
        bidRequestId?: string;
        taskId?: string;
        bidProjectId?: number;
        bidRequestIds?: string[];
        estimateId?: string;
        role?: PartnerContactProjectRole;
      };
    }
  | { errorType: typeof PartnerIntegrationErrorType.ESTIMATE_PDF_NOT_FOUND; data: { bidRequestId: string } }
  | {
      errorType: typeof PartnerIntegrationErrorType.EMAIL_NOTIFICATION_DATA_MISSING;
      data: { bidRequestId: string };
    }
  | { errorType: typeof PartnerIntegrationErrorType.INVALID_TASK_STATE; data: { projectId: number } }
  | { errorType: typeof PartnerIntegrationErrorType.INSURANCE_APPROVAL_TASK_NOT_FOUND; data: { projectId: number } }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_HANDLE_INSURANCE_FILES;
      data: { failedFiles: { id: string }[] };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_UPLOAD_FILES;
      data: { failedFiles: { id?: string; name: string }[] };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.CLAIM_NUMBER_NOT_FOUND;
      data: { bidRequestId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.ACTIVE_HIC_NOT_FOUND;
      data: { projectId?: number; bidQuoteId?: string; quoteId?: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.HIC_NOT_SIGNED;
      data: { projectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.TASK_NOT_FOUND;
      data: { projectId: number } | { taskId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_PROCESS_FILES;
      data: { failedFiles: { name: string }[] };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.AUTOMATION_NOT_FOUND;
      data: { automationId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INVALID_AUTOMATION_DATA;
      data: { automationId: string; error: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.AUTOMATION_REQUEUE_NOT_ALLOWED;
      data: { taskId: string; automationId: string; reason: TaskAutomationRequeueBlockedReason };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.AUTOMATION_REQUEUE_RACED;
      data: { taskId: string; automationId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.AUTOMATION_REQUEUE_SEND_FAILED;
      data: { taskId?: string; automationId: string; invoiceAutomationId?: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.AUTOMATION_SEND_FAILED;
      data: { automationId: string; projectId?: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INVALID_ARGUMENTS;
      data: undefined;
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.PROJECT_NOT_CHILD_OF_ANY_BID;
      data: { projectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.PROJECT_NOT_CHILD_OF_BID;
      data: { projectId: number; bidId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.MANUAL_RESOLUTION_REASON_REQUIRED;
      data: { message: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_UPDATE_AUTOMATION_STATUS;
      data: { automationId: string; status: SunrunAutomationStatus };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.NO_ACTIVE_QUOTE;
      data: { requestForQuoteId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.QUOTE_NOT_ACTIVE;
      data: { bidQuoteId?: string; quoteId?: string; activeQuoteId: string | null };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_RESOLVE_AUTOMATIONS;
      data: { automationIds: string[] };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_CREATE_SVIX_APP;
      data: undefined;
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_FETCH_OPENAPI_SPEC;
      data: undefined;
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.UNSUPPORTED_PARTNER_FLOW;
      data: { partnerFlow: PartnerFlow };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INVALID_TASK_TYPE;
      data: { taskId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INSURANCE_ELECTIVE_WORK_CREATED_TASK_NOT_FOUND;
      data: { projectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.ORGANIZATION_NOT_FOUND;
      data: { organizationId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.EXTERNAL_QUOTE_NOT_FOUND;
      data: { bidRequestId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.SHARINPIX_IMAGE_NOT_FOUND;
      data: { url: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.SHARINPIX_NO_CLICKABLE_LINK;
      data: { url: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INVALID_EXTERNAL_SERVICE_TYPE;
      data: undefined;
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INVALID_PARTNER_TASK_STATUS;
      data: { partnerTaskId: string; accountId: string; status: string | undefined };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.INVALID_WORK_ORDER_STATUS;
      data: { workOrderId: string; accountId: string; status: string | undefined; validStatuses: readonly string[] };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WORK_ORDER_NOT_FOUND;
      data: { workOrderId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WORK_ORDER_TYPE_NOT_FOUND;
      data: { workOrderId: string; sunrunRecordId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.ESTIMATE_NOT_FOUND;
      data: { estimateId?: string; bidRequestId?: string; taskId?: string; requestForQuoteId?: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.FAILED_TO_CREATE_MESSAGE_IN_REMI;
      data: {
        externalId: string;
        refSource: string;
        refId: string;
        overrideUnresolutionCheck?: boolean;
        networkError?: {
          status?: number;
          statusText?: string;
          code?: string;
          message: string;
        };
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WESTHILL_BID_SYNC_FAILED;
      data: {
        assignmentId: string;
        networkError: {
          status?: number;
          statusText?: string;
          code?: string;
          message: string;
        };
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.XA_FILE_FETCH_FAILED;
      data: {
        fileId: string;
        networkError: {
          status?: number;
          statusText?: string;
          code?: string;
          message: string;
        };
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WESTHILL_SET_PARTNER_CONTACT_FAILED;
      data: {
        assignmentId: string;
        role: PartnerContactProjectRole;
        networkError: {
          status?: number;
          statusText?: string;
          code?: string;
          message: string;
        };
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WESTHILL_ASSIGNMENT_SCRAPE_FAILED;
      data: {
        assignmentId: string;
        mfn: string;
        phase: "clientPolicyInfo" | "assignmentContacts" | "collaborationDate" | "correctionRow" | "estimateMismatch";
        causeName: string;
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WESTHILL_XA_DOCUMENT_CHECK_DENIED;
      data: { externalRef: string; checkId: string; callerOrgId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WESTHILL_XA_DOCUMENT_UNCHECKABLE_NOTE_FAILED;
      data: { projectId: number; taskId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.WESTHILL_XA_DOCUMENT_CHECK_FAILED;
      data: { mfn?: string; checkId?: string; stage: "PULL" | "REPORT" };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_SITE_SURVEY_SUBMISSION_NOT_FOUND;
      data: { submissionId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_SUBMISSION_SYNC_FAILED;
      data: { submissionId: string; cause: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.ENSITE_SITE_SURVEY_SUBMISSION_NOT_FOUND;
      data: { submissionId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.SUNRUN_RESCHEDULE_TASK_EVENT_NOT_FOUND;
      data: { taskEventId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.ENSITE_SUBMISSION_MISSING_SLOT;
      data: { submissionId: string; remiProjectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.HOVER_TOKEN_REFRESH_FAILED;
      data: undefined;
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.HOVER_JOB_NOT_FOUND;
      data: { hoverJobId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.HOVER_SYNC_FILES_WORK_SITE_MISMATCH;
      data: { hoverJobId: number; expectedWorkSiteId: number; actualWorkSiteId: number | null };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.HOVER_AUTO_ASSIGN_PARTIAL_FAILURE;
      data: {
        failedHoverJobIds: number[];
        failureClassifications: { hoverJobId: number; classification: "permanent" | "transient" | "auth-missing" }[];
        contractorOrgId: string;
        expectedHoverOrgId: number;
        totalAttempts: number;
        firstErrorMessage: string;
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.HOVER_UNKNOWN_MODEL_STATE;
      data: { hoverModelId: number; rawState: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.ATTACHMENT_BODY_TOO_LARGE;
      data: { parentId: string; name: string; fileSizeBytes: number; maxBytes: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.UNSUPPORTED_SITE_VISIT_CONTRACTOR_ORG;
      data: { contractorOrgId: string; projectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.SITE_VISIT_AVAILABILITY_INTEGRATION_MISMATCH;
      data: { expected: string; received: string } & ({ submissionId: string } | { remiProjectId: number });
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_REINSPECTION_MISSING_DATE_CREATED;
      data: {
        rdclJobId: string;
        parentRdclJobId: string;
        dateCreated: string | null;
        reason: "absent" | "null" | "malformed";
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_REINSPECTION_CUTOFF_NOT_CONFIGURED;
      data: undefined;
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_INSPECTION_PARSE_DRIFT;
      data: {
        rdclJobId: string;
        degradedLeafCount: number;
        vanishedContainerCount: number;
        droppedContainerCount: number;
        inspectionBodyVented: boolean;
      };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_REINSPECTION_NO_PENDING_MAPPING;
      data: { rdclJobId: string; parentRdclJobId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.RDCL_REINSPECTION_NOTIFICATION_EMAIL_NOT_CONFIGURED;
      data: { remiProjectId: number; parentRemiProjectId: number };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.SALESFORCE_CREDENTIALS_UNRECOVERABLE;
      data: { partnerAccountId: SunrunAccountId; message: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.PARTNER_TASK_INGEST_IN_PROGRESS;
      data: { partnerTaskId: string };
    }
  | {
      errorType: typeof PartnerIntegrationErrorType.PARTNER_TASK_ATTACHMENT_DOWNLOAD_INCOMPLETE;
      data: { projectId: number; transientFailureCount: number };
    };

export const partnerIntegrationErrorDetails: Record<PartnerIntegrationErrorType, ErrorDetails> = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  BID_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Bid not found" },
  PROJECT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Project not found",
  },
  ACCOUNT_ID_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Account ID not found",
  },
  REQUEST_FOR_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Request for quote not found",
  },
  QUOTE_VERSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Quote version not found",
  },
  FILE_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "File not found" },
  FILE_NOT_FOUND_IN_S3: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "File not found in S3",
  },
  MULTIPLE_BID_REQUESTS_FOUND: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Multiple bid requests found for assignment",
  },
  HOMEOWNER_HAS_NO_PHONE_NUMBER: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Homeowner has no phone number",
  },
  BID_REQUEST_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Bid request not found",
  },
  ESTIMATE_PDF_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate PDF not found",
  },
  EMAIL_NOTIFICATION_DATA_MISSING: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Email notification data missing",
  },
  INVALID_TASK_STATE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Task not found",
  },
  INSURANCE_APPROVAL_TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Insurance approval task not found",
  },
  FAILED_TO_HANDLE_INSURANCE_FILES: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to handle insurance files",
  },
  FAILED_TO_UPLOAD_FILES: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to upload files",
  },
  CLAIM_NUMBER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Claim number not found",
  },
  ACTIVE_HIC_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Active HIC not found",
  },
  HIC_NOT_SIGNED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "HIC not signed",
  },
  TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Task not found",
  },
  FAILED_TO_PROCESS_FILES: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to process files",
  },
  FAILED_TO_CREATE_SVIX_APP: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to create Svix app",
  },
  AUTOMATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Automation not found",
  },
  INVALID_AUTOMATION_DATA: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid automation data",
  },
  AUTOMATION_REQUEUE_NOT_ALLOWED: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Automation cannot be re-queued from the task in its current state",
  },
  AUTOMATION_REQUEUE_RACED: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Automation was re-queued concurrently by another request",
  },
  AUTOMATION_REQUEUE_SEND_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Re-queued automation message could not be sent to SQS",
  },
  AUTOMATION_SEND_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Automation message could not be sent to SQS",
  },
  INVALID_ARGUMENTS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid arguments",
  },
  PROJECT_NOT_CHILD_OF_ANY_BID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project ID must be a child project of a bid when Bid ID is not provided",
  },
  PROJECT_NOT_CHILD_OF_BID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Project ID is not a child project of the specified bid",
  },
  MANUAL_RESOLUTION_REASON_REQUIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "A resolution reason is required when creating a manually resolved automation",
  },
  FAILED_TO_UPDATE_AUTOMATION_STATUS: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to update automation status",
  },
  NO_ACTIVE_QUOTE: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No active quote found",
  },
  QUOTE_NOT_ACTIVE: {
    classification: ErrorClassification.INVALID_STATE_ERROR,
    sentryDescription: "Quote is not the active quote for the request for quote",
  },
  FAILED_TO_RESOLVE_AUTOMATIONS: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to resolve automations",
  },
  FAILED_TO_FETCH_OPENAPI_SPEC: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to fetch OpenAPI spec",
  },
  UNSUPPORTED_PARTNER_FLOW: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Unsupported partner flow",
  },
  INVALID_TASK_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid task type",
  },
  INSURANCE_ELECTIVE_WORK_CREATED_TASK_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Insurance elective work created task not found",
  },
  ORGANIZATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Organization not found",
  },
  EXTERNAL_QUOTE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "External quote not found",
  },
  SHARINPIX_IMAGE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Could not find file id while downloading photo from sharinpix",
  },
  SHARINPIX_NO_CLICKABLE_LINK: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "SharinPix page had no visible clickable link for photo navigation",
  },
  INVALID_EXTERNAL_SERVICE_TYPE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid external service type",
  },
  INVALID_PARTNER_TASK_STATUS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid partner task status received from Sunrun",
  },
  INVALID_WORK_ORDER_STATUS: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid work order status received from Sunrun",
  },
  WORK_ORDER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work order not found",
  },
  WORK_ORDER_TYPE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Work order Type__c not found on Sunrun record",
  },
  ESTIMATE_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Estimate not found",
  },
  FAILED_TO_CREATE_MESSAGE_IN_REMI: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to create message in Remi",
  },
  WESTHILL_BID_SYNC_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to sync Westhill XactAnalysis bid to Oddjob",
  },
  XA_FILE_FETCH_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to fetch Remi file bytes for XactAnalysis upload",
  },
  WESTHILL_SET_PARTNER_CONTACT_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to sync Westhill XactAnalysis partner contact to Oddjob",
  },
  WESTHILL_ASSIGNMENT_SCRAPE_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to scrape Westhill XactAnalysis assignment detail page",
  },
  WESTHILL_XA_DOCUMENT_CHECK_DENIED: {
    classification: ErrorClassification.AUTHORIZATION_ERROR,
    sentryDescription: "Caller is not authorized to report this Westhill XactAnalysis document check",
  },
  WESTHILL_XA_DOCUMENT_UNCHECKABLE_NOTE_FAILED: {
    classification: ErrorClassification.UNEXPECTED_ERROR,
    sentryDescription: "Failed to note why a Westhill documents attached task can't be checked in XactAnalysis",
  },
  WESTHILL_XA_DOCUMENT_CHECK_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Failed to pull or report a Westhill XactAnalysis document check",
  },
  RDCL_SITE_SURVEY_SUBMISSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "RDCL site survey submission not found",
  },
  RDCL_SUBMISSION_SYNC_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "RDCL submission sync to RDCL API failed — no retry mechanism, requires manual intervention",
  },
  ENSITE_SITE_SURVEY_SUBMISSION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Ensite site survey submission not found",
  },
  SUNRUN_RESCHEDULE_TASK_EVENT_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Sunrun reschedule task event not found or not a task submission",
  },
  ENSITE_SUBMISSION_MISSING_SLOT: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "Ensite submission reached sync with no bookable slot — ingest's NO_BOOKABLE_SLOT guard should have rejected it, so the guard and the sync-time slot derivation have drifted apart",
  },
  HOVER_TOKEN_REFRESH_FAILED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Hover OAuth token refresh failed at resync script start",
  },
  HOVER_JOB_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Hover job row was not found at file-sync time — possible deletion race or stale event",
  },
  HOVER_SYNC_FILES_WORK_SITE_MISMATCH: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "Hover file sync was requested with a workSiteId that disagrees with the persisted Hover job row",
  },
  HOVER_AUTO_ASSIGN_PARTIAL_FAILURE: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "Hover auto-assign invite failed for one or more jobs in the batch — Inngest will retry; investigate if onFailure fires after retries exhaust",
  },
  HOVER_UNKNOWN_MODEL_STATE: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "Hover returned a model state outside the known vocabulary; persisted as-is and treated as in-flight. Add it to @remi/enums/hover-model-state if legitimate.",
  },
  ATTACHMENT_BODY_TOO_LARGE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Attachment body exceeds Salesforce's 25MB Attachment.Body limit",
  },
  UNSUPPORTED_SITE_VISIT_CONTRACTOR_ORG: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Site-visit availability requested for a contractor org without a partner integration",
  },
  SITE_VISIT_AVAILABILITY_INTEGRATION_MISMATCH: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Selected site-visit availability blob's integration does not match the booking partner",
  },
  RDCL_REINSPECTION_MISSING_DATE_CREATED: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "RDCL reinspection job arrived without a readable dateCreated, so the ingest cutoff cannot be applied — the job is skipped permanently, and re-evaluated on every sync run, unless RDCL starts sending the field",
  },
  RDCL_REINSPECTION_CUTOFF_NOT_CONFIGURED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription:
      "RDCL reinspection detection is enabled but no ingest cutoff is set, so no reinspection will bind until RDCL_REINSPECTION_CUTOFF_AT is configured",
  },
  RDCL_INSPECTION_PARSE_DRIFT: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "RDCL inspection body could not be fully modeled — the schema vented the whole body or dropped a container, so the mapping will silently miss captured data",
  },
  RDCL_REINSPECTION_NO_PENDING_MAPPING: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription:
      "No open reinspection under the parent project matched an RDCL reinspection job — RDCL raised a reinspection nobody requested, or the one that was requested is already bound or cancelled; job skipped and retried next sync run",
  },
  RDCL_REINSPECTION_NOTIFICATION_EMAIL_NOT_CONFIGURED: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription: "No RDCL reinspection recipient is configured, so the reinspection request was not emailed",
  },
  SALESFORCE_CREDENTIALS_UNRECOVERABLE: {
    classification: ErrorClassification.DEPENDENCY_ERROR,
    sentryDescription:
      "Salesforce rejected the Sunrun service-account credentials with an expired password; token refresh cannot recover until the password is reset",
  },
  PARTNER_TASK_INGEST_IN_PROGRESS: {
    classification: ErrorClassification.CONFLICT_ERROR,
    sentryDescription: "Partner task ingest already in progress for this partner task",
  },
  PARTNER_TASK_ATTACHMENT_DOWNLOAD_INCOMPLETE: {
    classification: ErrorClassification.INTEGRATION_ERROR,
    sentryDescription: "Partner task attachment downloads failed transiently and will be retried",
  },
};
